import { useState, useEffect, useRef, useCallback, useReducer } from "react";
import "./index.css";
import { colors, type Node, type GridConfig } from "./theme";
import { Nav, WaitlistModal, type WaitlistPresets } from "./components";
import {
  Hero,
  Stats,
  Technology,
  Features,
  CustomerPaths,
  CTA,
  Footer,
  ArchitectureMap,
} from "./sections";
import {
  themeReducer,
  initialThemeState,
  selectIsDark,
  selectIsLocked,
  type ThemeMode,
} from "./themeState";
import { useIdleDetection } from "./useIdleDetection";

export function App() {
  const [themeState, dispatch] = useReducer(themeReducer, initialThemeState);
  const isDark = selectIsDark(themeState);
  const isLocked = selectIsLocked(themeState);
  const wakeCount = themeState.wakeCount;
  const themeMode: ThemeMode = themeState.mode;

  const [hasInteracted, setHasInteracted] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [awakened, setAwakened] = useState<Set<number>>(new Set());
  const [activeNode, setActiveNode] = useState(-1);
  const [heroHeight, setHeroHeight] = useState(800);
  const [demoActiveNodes, setDemoActiveNodes] = useState([3, 10, 17]);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistPresets, setWaitlistPresets] = useState<WaitlistPresets>({});
  const [showArchitectureMap, setShowArchitectureMap] = useState(false);
  const [showWakingPill, setShowWakingPill] = useState(false);

  // Refs for fresh values in callbacks (avoids stale closures)
  const awakenedRef = useRef(awakened);
  awakenedRef.current = awakened;

  // Cascade timeout refs for cancellation
  const cascadeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const openWaitlist = (presets: WaitlistPresets = {}) => {
    setWaitlistPresets(presets);
    setWaitlistOpen(true);
  };

  const cycleTheme = () => dispatch({ type: "CYCLE" });

  const clearCascade = useCallback(() => {
    cascadeTimersRef.current.forEach(clearTimeout);
    cascadeTimersRef.current = [];
  }, []);

  const handleGoIdle = useCallback(() => {
    clearCascade();
    const sorted = [...awakenedRef.current].sort((a, b) => b - a);
    sorted.forEach((nodeId, i) => {
      const timer = setTimeout(() => {
        setAwakened((prev) => {
          const next = new Set(prev);
          next.delete(nodeId);
          return next;
        });
      }, i * 120);
      cascadeTimersRef.current.push(timer);
    });
  }, [clearCascade]);

  const resetIdleTimer = useIdleDetection(dispatch, handleGoIdle, isLocked);

  // Handle scroll (just track position, idle detection is in hook)
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Show waking pill when wakeCount increments (only in auto mode)
  useEffect(() => {
    if (wakeCount > 0 && !isLocked) {
      // Small delay so pill appears after transition begins
      const showTimer = setTimeout(() => setShowWakingPill(true), 300);
      // Hide pill after 2.5s
      const hideTimer = setTimeout(() => setShowWakingPill(false), 2800);
      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
        setShowWakingPill(false);
      };
    }
  }, [wakeCount, isLocked]);

  // Randomize demo active nodes on wake/sleep
  useEffect(() => {
    const newActiveNodes: number[] = [];
    while (newActiveNodes.length < 3) {
      const rand = Math.floor(Math.random() * 21);
      if (!newActiveNodes.includes(rand)) {
        newActiveNodes.push(rand);
      }
    }
    setDemoActiveNodes(newActiveNodes);
  }, [isDark]);

  // Track hero height for scroll-based visibility
  useEffect(() => {
    const updateHeroHeight = () => {
      setHeroHeight(window.innerHeight);
    };
    updateHeroHeight();
    window.addEventListener("resize", updateHeroHeight);
    return () => window.removeEventListener("resize", updateHeroHeight);
  }, []);

  // Hero callbacks
  const handleNodeHover = useCallback((nodeId: number) => {
    setActiveNode(nodeId);
    setAwakened((prev) => new Set(prev).add(nodeId));
  }, []);

  const handleNodeLeave = useCallback(() => {
    setActiveNode(-1);
  }, []);

  const handleFirstInteraction = useCallback((nodes: Node[], gridConfig: GridConfig) => {
    setHasInteracted(true);
    const { cols, rows } = gridConfig;
    const centerRow = Math.floor(rows / 2);
    const centerCol = Math.floor(cols / 2);

    nodes.forEach((node, i) => {
      const distFromCenter =
        Math.abs(node.row - centerRow) + Math.abs(node.col - centerCol);
      setTimeout(() => {
        setAwakened((prev) => new Set([...prev, i]));
      }, distFromCenter * 120);
    });
  }, []);

  const statsVisible = scrollY > heroHeight * 0.3;
  const featuresVisible = scrollY > heroHeight * 0.6;

  return (
    <div
      className="min-h-screen transition-colors duration-[2500ms] overflow-x-hidden"
      style={{
        backgroundColor: isDark ? colors.base03 : colors.base3,
        color: isDark ? colors.base0 : colors.base00,
        fontFamily:
          "'IBM Plex Sans', 'SF Pro Display', -apple-system, sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');

        .text-gradient-light {
          background: linear-gradient(135deg, ${colors.blue} 0%, ${colors.magenta} 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .text-gradient-dark {
          background: linear-gradient(135deg, ${colors.cyan} 0%, ${colors.magenta} 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .idle-indicator {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>

      <Nav
        isDark={isDark}
        scrollY={scrollY}
        onOpenWaitlist={() => openWaitlist({ betaTester: true })}
        themeMode={themeMode}
        onCycleTheme={cycleTheme}
      />

      {/* Dark mode indicator - only show when in auto mode */}
      {isDark && !isLocked && (
        <div
          className="fixed top-16 md:top-24 right-4 md:right-8 z-40 flex items-center gap-2 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full idle-indicator"
          style={{
            backgroundColor: `${colors.base02}`,
            border: `2px solid transparent`,
            backgroundImage: `linear-gradient(${colors.base02}, ${colors.base02}), linear-gradient(135deg, ${colors.magenta}, ${colors.green})`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
        >
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: colors.cyan }}
          />
          <span
            className="text-xs font-medium"
            style={{ color: colors.base01 }}
          >
            Lights off — move to wake
          </span>
        </div>
      )}

      {/* Waking indicator - show briefly when transitioning from dark to light */}
      {showWakingPill && (
        <div
          className="fixed top-16 md:top-24 right-4 md:right-8 z-40 flex items-center gap-2 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full animate-pulse"
          style={{
            backgroundColor: colors.base2,
            border: `2px solid transparent`,
            backgroundImage: `linear-gradient(${colors.base2}, ${colors.base2}), linear-gradient(135deg, ${colors.orange}, ${colors.cyan})`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
        >
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: colors.orange }}
          />
          <span
            className="text-xs font-medium"
            style={{ color: colors.base1 }}
          >
            Waking page, activity detected
          </span>
        </div>
      )}

      <Hero
        isDark={isDark}
        hasInteracted={hasInteracted}
        awakened={awakened}
        activeNode={activeNode}
        onNodeHover={handleNodeHover}
        onNodeLeave={handleNodeLeave}
        onFirstInteraction={handleFirstInteraction}
        onActivity={resetIdleTimer}
        onToggleIdle={() => dispatch({ type: "TOGGLE_IDLE" })}
        onOpenWaitlist={openWaitlist}
        onOpenArchitecture={() => setShowArchitectureMap(true)}
      />

      <Stats isDark={isDark} isVisible={statsVisible} />

      <Technology
        isDark={isDark}
        isVisible={featuresVisible}
        demoActiveNodes={demoActiveNodes}
      />

      <Features isDark={isDark} />

      <CustomerPaths isDark={isDark} onOpenWaitlist={openWaitlist} />

      <CTA isDark={isDark} onOpenWaitlist={openWaitlist} />

      <Footer isDark={isDark} />

      <WaitlistModal
        isOpen={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        isDark={isDark}
        presets={waitlistPresets}
      />

      {showArchitectureMap && (
        <ArchitectureMap
          isDark={isDark}
          onClose={() => setShowArchitectureMap(false)}
        />
      )}
    </div>
  );
}

export default App;
